import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs';
import { IResizedColumn } from '../../common/models/table';
import { IContext } from '../../context/models/context';
import { IColumnDefinition } from '../../common/models/column-definition';
import { LandlordTableColumnsHttpService } from './landlord-table-columns-http.service';
import * as landlordTableColumnActions from '../actions/landlord-table-columns';
import { select, Store } from '@ngrx/store';
import { tap, catchError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LandlordTableService } from './landlord-table.service';


@Injectable()
export class LandlordTableColumnService {

  constructor(private readonly landlordTableColumnHttpService: LandlordTableColumnsHttpService,
    private readonly store: Store<any>,
    private readonly messageService: ToastrService,
    private readonly landlordTableService: LandlordTableService) { }

  loadColumns(): void {
    this.store.dispatch(new landlordTableColumnActions.Load());
    this.landlordTableColumnHttpService.getLandlordTableColumns().pipe(
      tap((columns: IColumnDefinition[]) => {
        this.store.dispatch(new landlordTableColumnActions.LoadComplete(columns));
      }),
      catchError(err => {
        this.store.dispatch(new landlordTableColumnActions.LoadError(err));
        this.messageService.error('An error occurred whilst trying to retrieve the columns', 'Error', { disableTimeOut: true });
        return [];
      }),
      take(1)
    ).subscribe();
  }

  getLandlordColumns(): Observable<IColumnDefinition[]> {
    return this.store.pipe(select((state) => state.landlordTableAvailableColumns.availableColumns));
  }

  updateColumns(columns: IColumnDefinition[], context: IContext): void {
    this.store.dispatch(new landlordTableColumnActions.UpdateColumns());
    this.landlordTableColumnHttpService.putLandlordTableColumns(columns).pipe(
      tap(() => {
        this.store.dispatch(new landlordTableColumnActions.UpdateColumnsComplete());
        this.landlordTableService.loadLandlords(context);
        this.loadColumns();
      }),
      catchError(err => {
        this.store.dispatch(new landlordTableColumnActions.UpdateColumnsError(err));
        this.messageService.error('An error occurred whilst trying to update the columns', 'Error', { disableTimeOut: true });
        return [];
      }),
      take(1)
    ).subscribe();
  }

  reorderColumns(orderedColumns: string[]): void {
    this.store.dispatch(new landlordTableColumnActions.ReorderColumns());
    this.landlordTableColumnHttpService.reorderLandlordTableColumns(orderedColumns).pipe(
      tap(() => {
        this.store.dispatch(new landlordTableColumnActions.ReorderColumnsComplete());
      }),
      catchError(err => {
        this.messageService.error('An error occurred whilst trying to reorder the columns', 'Error', { disableTimeOut: true });
        return [];
      }),
      take(1)
    ).subscribe();
  }

  loadDefaults(context: IContext): void {
    this.store.dispatch(new landlordTableColumnActions.ResetToDefault());
    this.landlordTableColumnHttpService.setTableDefaultColumns().pipe(
      tap(() => {
        this.store.dispatch(new landlordTableColumnActions.ResetToDefaultComplete());
        this.landlordTableService.loadLandlords(context);
      }),
      catchError(err => {
        this.messageService.error('An error occurred whilst trying to reset the columns', 'Error', { disableTimeOut: true });
        return [];
      }),
      take(1)
    ).subscribe();
  }

  resizeColumns(resizedColumn: IResizedColumn): void {
    this.store.dispatch(new landlordTableColumnActions.ResizeColumns());
    this.landlordTableColumnHttpService.resizeColumns(resizedColumn).pipe(
      tap(() => {
        this.store.dispatch(new landlordTableColumnActions.ResizeColumnsComplete());
      }),
      catchError(err => {
        this.store.dispatch(new landlordTableColumnActions.ResizeColumnsError(err));
        this.messageService.error('An error occurred whilst trying to resize the column', 'Error', { disableTimeOut: true });
        return [];
      }),
      take(1)
    ).subscribe();
  }

}
